import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Grid, Box, Button, CircularProgress, Typography } from '@mui/material';

import PageContainer from '@shared/components/container/PageContainer';
import Spinner from '@shared/components/spinner/Spinner';
import { AnimatedContent } from '@shared/components/AnimatedOutlet';

import EditCampaignSidebar from './EditCampaignSidebar/EditCampaignSidebar';
import { SidebarTabNames } from './constants';
import {
  CampaignDto,
  useCampaignUpdateControllerCampaignUpdateMutation,
  useCampaignSipTrunkControllerGetSipTrunkQuery,
  useCampaignControllerCampaignQuery,
  useCompanyControllerGetCompanyQuery,
} from '@shared/services/apiService/apiService';
import { RootState, useAppSelector } from '@shared/store';
import { useParams } from 'react-router-dom';
import { useEditCampaign } from '@features/Admin/Campaign/hooks/useEditCampaign';
import { useCampaignRealtimeSync } from '@features/Admin/Campaign/hooks/useCampaignRealtimeSync';
import { useEditCampaignLayout } from '@features/Admin/Campaign/hooks/useEditCampaignLayout';
import { useEditCampaignTabRouting } from '@features/Admin/Campaign/hooks/useEditCampaignTabRouting';
import { useCampaignStartValidation } from '@features/Admin/Campaign/hooks/useCampaignStartValidation';
import { useCampaignAutosave } from '@features/Admin/Campaign/hooks/useCampaignAutosave';
import { useCampaignStartStop } from '@features/Admin/Campaign/hooks/useCampaignStartStop';
import { CampaignRunStatusType } from '@shared/services/apiService/missing-types';
import { useTranslation } from 'react-i18next';
import { FormProvider, useFormState } from 'react-hook-form';
import { UserRolesEnum } from '@shared/constants/auth.constants';
import { useCampaignAgents } from '@features/Admin/Agent/hooks/useCampaignAgents';
import {
  mapCampaignAmbienceDraftToRequest,
  resolveAmbienceSettingsErrorMessage,
  showAmbienceSettingsErrorToast,
  showAmbienceSettingsSuccessToast,
  useAmbienceSettingsController,
} from '@features/Admin/AmbienceSettings';
import { EditCampaignTabContent } from './EditCampaignTabContent';
import { EditCampaignDialogs } from './EditCampaignDialogs';

export const EditCampaignPage: React.FC = () => {
  const { campaignId } = useParams<{ campaignId: string }>();
  const { t } = useTranslation();
  const currentUser = useAppSelector((state: RootState) => state.auth.authenticatedUser);
  const currentCompany = useAppSelector((state: RootState) => state.auth.authenticatedUserCompany);
  const [updateCampaignBackgroundSounds, { isLoading: isSavingAmbienceSettings }] =
    useCampaignUpdateControllerCampaignUpdateMutation();

  const [leadListChanged, setLeadListChanged] = useState(false);
  const [hasAttemptedAmbienceSettingsSave, setHasAttemptedAmbienceSettingsSave] = useState(false);
  const [isAgentCreateSyncing, setIsAgentCreateSyncing] = useState(false);
  const leadListChangedRef = useRef(leadListChanged);

  useEffect(() => {
    leadListChangedRef.current = leadListChanged;
  }, [leadListChanged]);

  const {
    data: campaignData,
    refetch,
    isLoading,
    error: campaignFetchError,
  } = useCampaignControllerCampaignQuery(
    { id: campaignId! },
    {
      skip: !campaignId,
      refetchOnMountOrArgChange: true,
    },
  );

  const isNotFound = useMemo(
    () =>
      Boolean(
        !campaignData &&
        campaignFetchError &&
        'status' in campaignFetchError &&
        campaignFetchError.status === 404,
      ),
    [campaignData, campaignFetchError],
  );

  const isSquadCampaign = !!campaignData?.squad;

  const activeTab = useEditCampaignTabRouting({ campaignData, isSquadCampaign });

  useCampaignRealtimeSync({
    campaignId,
    companyId: campaignData?.companyId,
  });

  useEditCampaignLayout(campaignData);

  const {
    data: campaignAgents,
    refetch: refetchCampaignAgents,
    isLoading: isLoadingCampaignAgents,
  } = useCampaignAgents(campaignId, isSquadCampaign || !campaignData || isNotFound);

  const agents = useMemo(() => {
    if (isSquadCampaign) {
      return campaignData?.squad?.agents || [];
    }
    return campaignAgents ?? [];
  }, [isSquadCampaign, campaignData, campaignAgents]);

  const hasAgents = useMemo(() => {
    if (isSquadCampaign) {
      return (campaignData?.squad?.agents?.length ?? 0) > 0;
    }
    if (agents.length > 0) {
      return true;
    }
    if (isAgentCreateSyncing || isLoadingCampaignAgents) {
      return (campaignData?.agentsCount ?? 0) > 0;
    }
    return false;
  }, [
    agents.length,
    campaignData?.agentsCount,
    isAgentCreateSyncing,
    isLoadingCampaignAgents,
    isSquadCampaign,
  ]);

  const handleAgentCreated = useCallback(async () => {
    setIsAgentCreateSyncing(true);

    try {
      await refetch();

      if (!isSquadCampaign) {
        await refetchCampaignAgents();
      }
    } finally {
      setIsAgentCreateSyncing(false);
    }
  }, [isSquadCampaign, refetch, refetchCampaignAgents]);

  const isAmbienceSettingsTab = activeTab === SidebarTabNames.AMBIENCE_SETTINGS;
  const isAmbienceSettingsReadOnly =
    currentUser?.role === UserRolesEnum.VIEWER ||
    campaignData?.status !== CampaignRunStatusType.INACTIVE;
  const ambienceSettingsDisableReason =
    campaignData?.status !== CampaignRunStatusType.INACTIVE
      ? t('campaignPage.ambienceSettings.disabledCampaign')
      : null;

  const {
    sounds: backgroundSounds,
    isLoadingSounds: isLoadingBackgroundSounds,
    draft: ambienceSettingsDraft,
    setDraft: setAmbienceSettingsDraft,
    resetDraft: resetAmbienceSettingsDraft,
    isDirty: isAmbienceSettingsDirty,
    hasValidationError: hasAmbienceSettingsValidationError,
    hasMissingSounds: hasAmbienceSettingsMissingSounds,
  } = useAmbienceSettingsController({
    mode: 'campaign',
    backgroundSounds: campaignData?.backgroundSounds,
    isReadOnly: isAmbienceSettingsReadOnly,
  });

  const { isSuccess: isCompanyFetched } = useCompanyControllerGetCompanyQuery(
    { id: (campaignData as unknown as CampaignDto)?.companyId },
    { skip: !(campaignData as unknown as CampaignDto)?.companyId },
  );

  const { data: campaignSipTrunk } = useCampaignSipTrunkControllerGetSipTrunkQuery(
    { id: campaignId! },
    { skip: !campaignId },
  );

  const { saveCampaignForm, formMethods, responseLoadingFlag } = useEditCampaign({
    CustomersListChanged: leadListChangedRef,
  });

  const { control, setError, clearErrors } = formMethods;
  const { errors: formStateErrors } = useFormState({ control });

  const {
    isRunningLoading,
    isStoppingLoading,
    showScheduleWarningDialogFlag,
    setShowScheduleWarningDialogFlag,
    showUsageLimitWarningDialog,
    setShowUsageLimitWarningDialog,
    showUsageLimitBlockedDialog,
    setShowUsageLimitBlockedDialog,
    timeUntilStartCampaignRun,
    closeScheduleDialog,
    runCampaign,
    handleUsageLimitWarningConfirm,
  } = useCampaignStartStop({ campaignData, refetch });

  const { issuedTabs, tabIssueMessages, forceValidate } = useCampaignStartValidation({
    campaignData,
    campaignSipTrunk,
    formStateErrors,
    hasAgents,
    isLoading,
    isLoadingCampaignAgents,
    isRunningLoading,
    isStoppingLoading,
    currentUserRole: currentUser?.role,
    isNotFound,
    setError,
    clearErrors,
  });

  useCampaignAutosave({
    campaignData,
    formMethods,
    saveCampaignForm,
    leadListChangedRef,
    setLeadListChanged,
    refetch,
    forceValidate,
  });

  useEffect(() => {
    if (!hasAmbienceSettingsValidationError && !hasAmbienceSettingsMissingSounds) {
      setHasAttemptedAmbienceSettingsSave(false);
    }
  }, [hasAmbienceSettingsMissingSounds, hasAmbienceSettingsValidationError]);

  const handleAmbienceSettingsSave = useCallback(async () => {
    if (!campaignData?.id || !ambienceSettingsDraft || isAmbienceSettingsReadOnly) {
      return;
    }

    if (hasAmbienceSettingsValidationError || hasAmbienceSettingsMissingSounds) {
      setHasAttemptedAmbienceSettingsSave(true);
      return;
    }

    try {
      await updateCampaignBackgroundSounds({
        id: campaignData.id,
        campaignUpdateRequestDto: {
          backgroundSounds: mapCampaignAmbienceDraftToRequest(ambienceSettingsDraft),
        },
      }).unwrap();
      setHasAttemptedAmbienceSettingsSave(false);

      showAmbienceSettingsSuccessToast({
        title: t('common.saved'),
        description: t('campaignPage.ambienceSettings.saveSuccessDescription'),
      });

      await refetch();
    } catch (error) {
      showAmbienceSettingsErrorToast(
        resolveAmbienceSettingsErrorMessage(
          error,
          t('campaignPage.ambienceSettings.saveErrorDescription'),
        ),
      );
    }
  }, [
    ambienceSettingsDraft,
    campaignData?.id,
    hasAmbienceSettingsMissingSounds,
    hasAmbienceSettingsValidationError,
    isAmbienceSettingsReadOnly,
    refetch,
    t,
    updateCampaignBackgroundSounds,
  ]);

  const handleAmbienceSettingsCancel = useCallback(() => {
    setHasAttemptedAmbienceSettingsSave(false);
    resetAmbienceSettingsDraft();
  }, [resetAmbienceSettingsDraft]);

  if (activeTab === null || isLoading) {
    return <Spinner />;
  }

  const pageTitle = campaignData?.title?.trim() || t('campaignPage.editCampaign.editCampaign');

  return (
    <PageContainer title={pageTitle} companyName={currentCompany?.title}>
      <Box sx={{ display: 'flex', flexDirection: 'row', minHeight: '100%', height: 'max-content' }}>
        <EditCampaignSidebar
          activeTab={activeTab}
          issuedTabs={issuedTabs}
          tabIssueMessages={tabIssueMessages}
          campaignType={campaignData?.type}
          hasSquad={isSquadCampaign}
        />
        <Grid
          container
          sx={{
            overflow: 'hidden',
            flexDirection: 'column',
            flexWrap: 'nowrap',
            flex: 1,
            minWidth: 0,
          }}>
          {isAmbienceSettingsTab && campaignData ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: 3,
                px: 4,
                pt: 3,
              }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="h5" fontWeight={600}>
                  {t('campaignPage.ambienceSettings.title')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('campaignPage.ambienceSettings.description')}
                </Typography>
                {ambienceSettingsDisableReason ? (
                  <Typography variant="body2" color="warning.main">
                    {ambienceSettingsDisableReason}
                  </Typography>
                ) : null}
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                <Box sx={{ display: 'flex', gap: 2, flexShrink: 0 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleAmbienceSettingsSave}
                    disabled={
                      isAmbienceSettingsReadOnly ||
                      !isAmbienceSettingsDirty ||
                      isSavingAmbienceSettings ||
                      isLoadingBackgroundSounds ||
                      hasAmbienceSettingsValidationError ||
                      hasAmbienceSettingsMissingSounds
                    }>
                    {isSavingAmbienceSettings ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      t('action.save')
                    )}
                  </Button>
                  <Button
                    variant="outlined"
                    color="inherit"
                    onClick={handleAmbienceSettingsCancel}
                    disabled={
                      isAmbienceSettingsReadOnly ||
                      !isAmbienceSettingsDirty ||
                      isSavingAmbienceSettings
                    }>
                    {t('action.cancel')}
                  </Button>
                </Box>
                {hasAmbienceSettingsValidationError ? (
                  <Typography variant="body2" color="error.main" sx={{ mt: 1 }}>
                    {t('campaignPage.ambienceSettings.probabilityTotalError')}
                  </Typography>
                ) : null}
                {!hasAmbienceSettingsValidationError && hasAmbienceSettingsMissingSounds ? (
                  <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
                    {t('campaignPage.ambienceSettings.missingSoundWarning')}
                  </Typography>
                ) : null}
              </Box>
            </Box>
          ) : null}
          <FormProvider {...formMethods}>
            <AnimatedContent contentKey={activeTab} presenceMode="wait" instantExit>
              <EditCampaignTabContent
                activeTab={activeTab}
                campaignData={campaignData}
                campaignId={campaignId}
                isLoading={isLoading}
                isLoadingCampaignAgents={isLoadingCampaignAgents}
                isSquadCampaign={isSquadCampaign}
                isCompanyFetched={isCompanyFetched}
                agents={agents}
                onAgentCreated={handleAgentCreated}
                refetch={refetch}
                refetchCampaignAgents={refetchCampaignAgents}
                responseLoadingFlag={responseLoadingFlag}
                setLeadListChanged={setLeadListChanged}
                isLoadingBackgroundSounds={isLoadingBackgroundSounds}
                backgroundSounds={backgroundSounds}
                ambienceSettingsDraft={ambienceSettingsDraft}
                setAmbienceSettingsDraft={setAmbienceSettingsDraft}
                isAmbienceSettingsReadOnly={isAmbienceSettingsReadOnly}
                hasAttemptedAmbienceSettingsSave={hasAttemptedAmbienceSettingsSave}
              />
            </AnimatedContent>
          </FormProvider>
        </Grid>
      </Box>
      <EditCampaignDialogs
        showScheduleWarningDialogFlag={showScheduleWarningDialogFlag}
        onCloseScheduleDialog={closeScheduleDialog}
        setShowScheduleWarningDialogFlag={setShowScheduleWarningDialogFlag}
        timeUntilStartCampaignRun={timeUntilStartCampaignRun}
        onRunCampaign={runCampaign}
        showUsageLimitWarningDialog={showUsageLimitWarningDialog}
        setShowUsageLimitWarningDialog={setShowUsageLimitWarningDialog}
        onUsageLimitWarningConfirm={handleUsageLimitWarningConfirm}
        showUsageLimitBlockedDialog={showUsageLimitBlockedDialog}
        setShowUsageLimitBlockedDialog={setShowUsageLimitBlockedDialog}
      />
    </PageContainer>
  );
};
